import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleDayTripsFromVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-day-trips-from-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1-whistler": "Whistler – Alpine Adventure Paradise",
    "section-2-victoria": "Victoria – Historic Seaside Capital City",
    "section-3-squamish": "Squamish & the Sea-to-Sky Gondola",
    "section-4-north-shore": "North Shore Mountains",
    "section-5-bowen-island": "Bowen Island",
    "section-6-harrison": "Harrison Hot Springs",
    "section-7-sunshine-coast": "Sunshine Coast (Gibsons)",
    "section-8-steveston": "Steveston (Richmond)",
    "seasonal-recommendations": "Seasonal Recommendations",
    "budget-considerations": "Budget Considerations",
    "transportation-options": "Transportation Options",
    faq: "FAQ",
    "local-regulations": "Local Regulations & Best Practices",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.bcferries.com/",
      label: "BC Ferries – Official Schedules & Fares",
    },
    {
      url: "https://www.hellobc.com/",
      label: "Destination BC – Official Tourism Site",
    },
    {
      url: "https://bcparks.ca/",
      label: "BC Parks – Provincial Parks Info",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location references the corresponding section
  // using the same "id" as in the <h2> or <p ref={(el)=>setSpotRef("id",el)}>.
  const locations = [
    {
      id: "section-1-whistler",
      lat: 50.11639,
      lng: -122.95417,
      popupText: "Whistler Village Center",
    },
    {
      id: "section-2-victoria",
      lat: 48.42833,
      lng: -123.36472,
      popupText: "Victoria Inner Harbour",
    },
    {
      id: "section-3-squamish",
      lat: 49.70167,
      lng: -123.15889,
      popupText: "Downtown Squamish",
    },
    {
      id: "section-4-north-shore",
      lat: 49.34278,
      lng: -123.11222,
      popupText: "Capilano Suspension Bridge Park",
    },
    {
      id: "section-5-bowen-island",
      lat: 49.38300,
      lng: -123.38300,
      popupText: "Snug Cove, Bowen Island",
    },
    {
      id: "section-6-harrison",
      lat: 49.30000,
      lng: -121.78194,
      popupText: "Harrison Hot Springs Village",
    },
    {
      id: "section-7-sunshine-coast",
      lat: 49.40111,
      lng: -123.50750,
      popupText: "Gibsons Landing, Sunshine Coast",
    },
    {
      id: "section-8-steveston",
      lat: 49.12487,
      lng: -123.18647,
      popupText: "Steveston Wharf",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Best Day Trips from Vancouver: Top Scenic, Cultural & Adventure Getaways</title>
        <meta
          name="description"
          content="Explore the top day trips from Vancouver, from alpine adventures in Whistler to seaside escapes in Victoria. Get practical tips, costs, and local regulations."
        />
        <meta
          name="keywords"
          content="Vancouver, day trips, Whistler, Victoria, Squamish, Bowen Island, Harrison Hot Springs, Sunshine Coast, Steveston"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-day-trips-from-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Best Day Trips from Vancouver: Top Scenic, Cultural & Adventure Getaways"
        />
        <meta
          property="og:description"
          content="Explore the top day trips from Vancouver, covering nature, culture, and practical travel tips."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-day-trips-from-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-day-trips-from-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Day Trips from Vancouver: Top Scenic, Cultural & Adventure Getaways"
        />
        <meta
          name="twitter:description"
          content="Discover the best day trips from Vancouver, from alpine peaks to seaside towns, complete with coordinates and travel tips."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-day-trips-from-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Beautiful scenic view representing Vancouver day trips"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s location—tucked between the Pacific Ocean and the
              Coast Mountains—makes it the perfect base for incredible one-day
              adventures. Within a short journey, you can reach alpine
              playgrounds, charming island villages, historic coastal towns, and
              relaxing hot springs. This guide highlights the very best day
              trips from Vancouver, including the key attractions, travel tips,
              rough costs, and local regulations to help you plan a safe and
              unforgettable outing. Whether you’re a local craving a quick
              getaway or a visitor looking to experience British Columbia’s
              diverse landscapes, these top picks showcase the region’s natural
              beauty, culture, and activities.
            </p>

            {/* SECTION 1: Whistler */}
            <h2 id="section-1-whistler">Whistler – Alpine Adventure Paradise</h2>
            <p
              ref={(el) => setSpotRef("section-1-whistler", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Whistler Village Center</strong>{" "}
              <Marker
                coordY={50.11639}
                coordX={-122.95417}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Located about 120 km north of Vancouver, Whistler is a
              world-renowned mountain resort offering year-round alpine fun.
              During winter, it’s famous for skiing and snowboarding on Whistler
              Blackcomb, one of North America’s largest ski areas. In summer,
              visitors can enjoy scenic gondola rides, biking, ziplining,
              hiking, and more. The Peak 2 Peak Gondola between Whistler and
              Blackcomb Mountains provides breathtaking panoramas. Whistler
              Village itself is a bustling hub of shops, restaurants, and
              nightlife. Travel time is roughly 1.5–2 hours via the Sea-to-Sky
              Highway; several shuttle services also operate if you’re car-free.
              Expect costs from ~$25–$40 each way for buses, and ~$150+ for
              winter lift tickets. Remember to follow local regulations about
              snow tires in winter and keep an eye out for wildlife along the
              highway.
            </p>

            {/* SECTION 2: Victoria */}
            <h2 id="section-2-victoria">Victoria – Historic Seaside Capital City</h2>
            <p
              ref={(el) => setSpotRef("section-2-victoria", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Victoria Inner Harbour</strong>{" "}
              <Marker
                coordY={48.42833}
                coordX={-123.36472}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Situated on Vancouver Island, Victoria is British Columbia’s
              capital and is best known for its historic Parliament Buildings,
              charming harbor, and the famous Butchart Gardens. Enjoy museums,
              tea at the Fairmont Empress, or strolling along the scenic
              waterfront. The city’s mild climate makes it a lovely year-round
              destination. Getting there typically involves a BC Ferries trip
              from Tsawwassen to Swartz Bay (~1.5 hours), plus a drive or bus
              into downtown Victoria (30 minutes). Round-trip fares are ~$100+
              for a car with driver or ~$20–$25 for walk-on passengers each way.
              If time allows, consider a seaplane for a quick 35-minute flight
              from downtown Vancouver, though it’s more expensive. Local
              regulations include reserving ferry times (recommended in peak
              season) and following posted parking rules in downtown Victoria.
            </p>

            {/* SECTION 3: Squamish */}
            <h2 id="section-3-squamish">Squamish & the Sea-to-Sky Gondola</h2>
            <p
              ref={(el) => setSpotRef("section-3-squamish", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Downtown Squamish</strong>{" "}
              <Marker
                coordY={49.70167}
                coordX={-123.15889}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Halfway between Vancouver and Whistler, Squamish is an outdoor
              adventure hotspot. The Sea to Sky Gondola is a must-visit, offering
              sweeping views of Howe Sound and surrounding peaks. At the top,
              you’ll find hiking trails, a suspension bridge, and scenic
              lookouts. Nearby, Shannon Falls Provincial Park hosts one of BC’s
              tallest waterfalls. Popular activities include rock climbing, wind
              sports, and mountain biking. Travel by car or shuttle bus from
              Vancouver (~1 hour). Tickets for the gondola are around $60–$70,
              and Shannon Falls admission is free. Winter tire regulations apply
              if visiting October through March. Always follow local safety
              guidelines on trails to protect the environment and wildlife.
            </p>

            {/* SECTION 4: North Shore Mountains */}
            <h2 id="section-4-north-shore">North Shore Mountains (Capilano & Grouse)</h2>
            <p
              ref={(el) => setSpotRef("section-4-north-shore", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Capilano Suspension Bridge Park</strong>{" "}
              <Marker
                coordY={49.34278}
                coordX={-123.11222}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Just minutes from downtown Vancouver, the North Shore offers an
              immediate escape to lush rainforest and canyon views. Capilano
              Suspension Bridge Park features a 137-meter-long bridge suspended
              high over the Capilano River, plus cliff walks and treetop
              adventures. Grouse Mountain, known as “The Peak of Vancouver,” is
              another highlight with a skyride gondola, wildlife refuge, hiking
              (including the famous Grouse Grind), and winter skiing. Capilano
              tickets cost around $70 for adults, while Grouse Mountain’s basic
              gondola access is similar. Both sites are open year-round and are
              accessible via a free Capilano shuttle or public transit. Keep an
              eye out for local regulations about parking and noise bylaws.
            </p>

            {/* SECTION 5: Bowen Island */}
            <h2 id="section-5-bowen-island">Bowen Island – Tranquil Island Escape</h2>
            <p
              ref={(el) => setSpotRef("section-5-bowen-island", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Snug Cove, Bowen Island</strong>{" "}
              <Marker
                coordY={49.38300}
                coordX={-123.38300}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A quick 20-minute ferry ride from Horseshoe Bay, Bowen Island is
              a quaint community ideal for relaxing, light hikes, or kayaking.
              Explore Snug Cove’s shops and cafes, stroll through Crippen
              Regional Park to Killarney Lake, or hike Mount Gardner for
              panoramic views. Round-trip ferry fare for foot passengers is
              around $13. Local highlights include artisan studios, sandy
              beaches, and the calm pace of island life. Most activities here are
              low-cost or free, making it a great budget-friendly getaway. If
              you’re bringing a car, try to arrive at the ferry terminal early on
              weekends. Watch for local signage regarding parking and pet
              policies on trails.
            </p>

            {/* SECTION 6: Harrison Hot Springs */}
            <h2 id="section-6-harrison">Harrison Hot Springs – Lakeside Relaxation</h2>
            <p
              ref={(el) => setSpotRef("section-6-harrison", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Harrison Hot Springs Village</strong>{" "}
              <Marker
                coordY={49.3}
                coordX={-121.78194}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – About 1.5 hours east of Vancouver lies the resort town of
              Harrison Hot Springs, famous for its soothing hot spring pools,
              beautiful Harrison Lake, and mountain vistas. The public hot
              springs pool costs about $9 for entry, while the resort offers
              private pools for guests. Summer brings beach fun, paddleboarding,
              and an inflatable water park, while cooler months are perfect for
              a warm soak. Nearby farms and scenic backroads add to the charm.
              Car travel is the main option (no direct public transit). Watch for
              local safety rules at the public pools and heed any campfire bans
              in summer months.
            </p>

            {/* SECTION 7: Sunshine Coast (Gibsons) */}
            <h2 id="section-7-sunshine-coast">
              Sunshine Coast (Gibsons) – Artsy Coastal Town
            </h2>
            <p
              ref={(el) => setSpotRef("section-7-sunshine-coast", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Gibsons Landing, Sunshine Coast</strong>{" "}
              <Marker
                coordY={49.40111}
                coordX={-123.50750}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Accessed via a short ferry ride from Horseshoe Bay to Langdale,
              Gibsons is a charming seaside community celebrated for its
              galleries, unique shops, and the famous “Molly’s Reach” from the
              classic Canadian TV show *The Beachcombers*. Spend a day strolling
              the harbor, enjoying fish & chips, or hiking Soames Hill for a
              panoramic lookout over Howe Sound. Ferry fares are similar to
              Bowen Island, around $14 per foot passenger one-way (and free on
              the return). The Sunshine Coast has a laid-back vibe; respect
              posted speed limits on winding roads and be mindful of fire bans in
              the summer.
            </p>

            {/* SECTION 8: Steveston */}
            <h2 id="section-8-steveston">Steveston (Richmond) – Historic Fishing Village</h2>
            <p
              ref={(el) => setSpotRef("section-8-steveston", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Steveston Wharf</strong>{" "}
              <Marker
                coordY={49.12487}
                coordX={-123.18647}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Located on the southwestern corner of Richmond, Steveston is
              where the Fraser River meets the ocean. Known for its historic
              Gulf of Georgia Cannery, fresh seafood off the fishing boats, and
              scenic waterfront, it’s a fantastic urban day trip requiring just
              30–40 minutes by car (or transit) from downtown Vancouver. Stroll
              the boardwalk, enjoy fish & chips, or sign up for a whale-watching
              tour in season. Admission to the cannery is ~$12.50 for adults, and
              whale-watching tours start around $150. Parking is free but can
              fill up on sunny weekends. Follow local rules about fishing
              licenses if you plan to drop a line, and be mindful of the strong
              river currents when near the docks.
            </p>

            {/* Seasonal Recommendations */}
            <h2 id="seasonal-recommendations">Seasonal Recommendations</h2>
            <p>
              - <strong>Summer (June–August):</strong> Ideal for outdoor
              adventures, water sports, and long daylight. Try Whistler
              sightseeing, Bowen Island paddling, or Sunshine Coast festivals.
            </p>
            <p>
              - <strong>Fall (Sept–Nov):</strong> Spectacular foliage, quieter
              crowds, and eagle-watching in Squamish. Victoria’s fall colors and
              Harrison’s hot springs are also delightful.
            </p>
            <p>
              - <strong>Winter (Dec–Feb):</strong> Whistler’s ski season, Grouse
              Mountain’s snow activities, and tranquil winter strolls on Bowen
              Island or Steveston. Check road conditions and winter tire rules.
            </p>
            <p>
              - <strong>Spring (Mar–May):</strong> Gardens bloom in Victoria,
              waterfalls flow heavily in Squamish, and mild weather invites
              comfortable hikes on the North Shore or Bowen Island.
            </p>

            {/* Budget Considerations */}
            <h2 id="budget-considerations">Budget Considerations</h2>
            <p>
              Many of these destinations are achievable on a tight budget by
              using public transit (where possible), picking cheaper or free
              attractions, and bringing your own meals. Bowen Island and
              Steveston are particularly budget-friendly. At the other end,
              luxury travelers can opt for seaplane rides or private tours.
              Whistler, for instance, can be more expensive if you add
              high-end dining, spa visits, or skiing. Consider splitting fuel or
              car-rental costs with friends for the further destinations.
            </p>

            {/* Transportation Options */}
            <h2 id="transportation-options">Transportation Options</h2>
            <p>
              - <strong>Car:</strong> Offers the most flexibility. Watch for
              winter tire requirements on mountain highways (Sea-to-Sky, Hwy 1).
            </p>
            <p>
              - <strong>Public Transit:</strong> Easily reach places like
              Capilano or Steveston by bus and SkyTrain. Victoria is doable via
              BC Ferries Connector bus. Bowen Island & Sunshine Coast can be
              visited by express bus to Horseshoe Bay then walk-on ferry.
            </p>
            <p>
              - <strong>Ferries:</strong> Reserve for a vehicle on popular
              routes, especially if heading to Vancouver Island in peak season.
              Foot passengers rarely need reservations.
            </p>
            <p>
              - <strong>Shuttles & Tours:</strong> Several companies run
              day-trip tours to Whistler, Squamish, or wine tours in the Fraser
              Valley. Great if you prefer not to drive or want guided experiences.
            </p>

            {/* FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Are there car-free day trip options?</h3>
              <p>
                Yes! Steveston, Bowen Island, North Shore attractions, and even
                Victoria are accessible via public transit or shuttle buses.
                Check schedules in advance to ensure smooth connections.
              </p>

              <h3>2. Which spots are most family-friendly?</h3>
              <p>
                Bowen Island is great for easy walks and beaches; Steveston has
                a kid-friendly cannery tour and wharf. Whistler Village also
                caters to families, offering child-friendly attractions year-round.
              </p>

              <h3>3. Do I need to book ferry reservations?</h3>
              <p>
                It’s recommended if bringing a car to Victoria or on summer
                weekends. For foot passengers, reservations are generally not
                required except around holiday rush.
              </p>
            </div>

            {/* Local Regulations */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Always check for provincial park permits (some popular trails require
              free day-use passes in peak season), heed wildfire bans in summer,
              and follow “Leave No Trace” principles. Snow tires are mandatory on
              highways like the Sea-to-Sky from October to March. In coastal
              areas or near wildlife, avoid feeding animals. Ferry travelers must
              exit their vehicles on enclosed decks during the crossing for
              safety. If fishing, secure the appropriate license. Respect local
              speed limits and parking regulations—especially in smaller
              communities like Bowen Island or Gibsons. 
            </p>

            {/* Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you crave alpine thrills, island tranquility, or cultural
              exploration, Vancouver’s surrounding regions deliver unmatched
              variety. A quick ferry ride can lead you to lush forests or
              centuries-old landmarks. A short drive north can put you among
              snowcapped peaks. By planning with the tips in this guide—like
              budgeting, transportation, and local rules—you can create a truly
              memorable day trip from Vancouver. So, pack your essentials, check
              the weather, and embark on an adventure to discover the breadth of
              British Columbia’s beauty, culture, and outdoor experiences—all in
              just a single day.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}